import { countUnicodeCodePoints } from './count-unicode-code-points'
import { createBluetoothChecksum } from './ttt-crc8'

/**
 * Check if given string is a valid email.
 *
 * @param {string} email - The string to be tested
 * @returns {boolean} Whether this is a valid email address or not
 */
function isEmail(email) {
    // eslint-disable-next-line max-len
    const re =
        /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/
    return re.test(email)
}

/**
 * Check if given string is a valid time.
 *
 * @param {string} time
 * @returns {boolean}
 */
function isValidTime(time) {
    const validTimeRegex = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9](\s(AM|PM|am|pm))?$/
    return Boolean(typeof time === 'string' && time.match(validTimeRegex))
}

/**
 * Validates that an array has at least one item, regardless of item type.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @returns {function(Object[]): (Error|null)}
 */
function validateArrayHasItem(errorMessage) {
    return array => (array && array.length ? null : new Error(errorMessage || null))
}

/**
 * Validates that an array equals to compared array.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {string} [comparedArray] - A compared array.
 * @returns {function(Object[]): (Error|null)}
 */
function validateArraysAreEquals(errorMessage, comparedArray) {
    return array => {
        const result =
            array.length === comparedArray.length && array.every((element, index) => element === comparedArray[index])
        return array && comparedArray && result ? new Error(errorMessage) : null
    }
}

/**
 * Validates that a bluetooth string is valid.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @returns {function(string): (Error|null)}
 */
function validateBluetooth(errorMessage) {
    return bluetoothString => {
        const pattern = /^([A-Fa-f0-9]{2}:){5}[A-Fa-f0-9]{2}$/
        return !bluetoothString || pattern.test(bluetoothString) ? null : new Error(errorMessage || null)
    }
}

/**
 * Validated that a obd bluetooth string is valid and contains the correct checksum.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @returns {function(string): (Error|null)}
 */
function validateBluetoothWithChecksum(errorMessage, mandatory = false) {
    return bluetoothString => {
        if (mandatory && !bluetoothString) {
            return new Error(errorMessage || null)
        }

        const pattern = /^([A-Fa-f0-9]{2}:){6}[A-Fa-f0-9]{2}$/
        let error = !bluetoothString || pattern.test(bluetoothString) ? null : new Error(errorMessage || null)

        if (!error && bluetoothString) {
            const bluetoothAddressWithoutChecksum = bluetoothString.substring(0, 17)
            const checksum = bluetoothString.substring(18, 20)
            const calculatedChecksum = createBluetoothChecksum(bluetoothAddressWithoutChecksum)
            if (checksum !== calculatedChecksum) {
                error = new Error(errorMessage || null)
            }
        }

        return error
    }
}

/**
 * Validates that an input has a maximum amount of bytes.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {number} maxLen - Max value for input value.
 * @returns {function(string): (Error|null)}
 */
function validateByteLength(errorMessage, maxLen) {
    return value => {
        if (typeof value !== 'string') {
            return new Error(errorMessage || null)
        }

        const byteLength = new Blob([...value]).size
        return value && byteLength <= maxLen ? null : new Error(errorMessage || null)
    }
}

/**
 * Validates that a value is an email.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @returns {function(string): (Error|null)}
 */
function validateEmail(errorMessage) {
    return value => (isEmail(value) ? null : new Error(errorMessage || null))
}

/**
 * Validates that a value exists and is true. A string of spaces is always false.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @returns {function(string): (Error|null)}
 */
function validateExists(errorMessage) {
    return value => (value && `${value}`.replace(' ', '').length ? null : new Error(errorMessage || null))
}

/**
 * Validates that an input is an integer and is within a given range (min and max values included).
 *
 * @param {string} [errorMessage] - The error message if value is not an Integer or not within given range.
 * @param {number} min - Min value for input value.
 * @param {number} max - Max value for input value.
 * @returns {function(number): (Error|null)}
 */
function validateIsIntegerRange(errorMessage, min, max) {
    return numValue => {
        const number = Number(numValue)
        if (!Number.isInteger(number)) {
            return new Error(errorMessage || null)
        }

        return number >= min && number <= max ? null : new Error(errorMessage || null)
    }
}

/**
 * Validates that an input is an integer and is within a given range (min and max values included) or null.
 *
 * @param {string} [errorMessage] - The error message if value is not an Integer or not within given range.
 * @param {number} min - Min value for input value.
 * @param {number} max - Max value for input value.
 * @returns {function(number): (Error|null)}
 */
function validateIsIntegerRangeOrNull(errorMessage, min, max) {
    return numValue => {
        if (!numValue && typeof numValue !== 'number') {
            return null
        }
        const number = Number(numValue)
        if (!Number.isInteger(number)) {
            return new Error(errorMessage || null)
        }

        return number >= min && number <= max ? null : new Error(errorMessage || null)
    }
}

/**
 * Validates that an input is an integer, greater than 0 or a representation thereof (can be a string).
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {number} minLen - Min value for input value.
 * @param {number} maxLen - Max value for input value.
 * @returns {function(string): (Error|null)}
 */
function validateIsPositiveNumber(errorMessage, minLen, maxLen) {
    return value => {
        const length = (value + '').replace('.', '').length
        const isNumber = !Number.isNaN(Number(value))
        const parsedValue = parseInt(value)

        return isNumber && Number.isInteger(parsedValue) && parsedValue >= 0 && length >= minLen && length <= maxLen
            ? null
            : new Error(errorMessage || null)
    }
}

/**
 * Validates that an input is a valid 2 digits format for minutes between zero and 59 minutes.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @returns {function(string): (Error|null)}
 */
function validateMinutes(errorMessage) {
    return value => {
        const min = 0
        const max = 59
        const numberValue = parseInt(value)
        const isNumber = !Number.isNaN(numberValue)

        return isNumber && numberValue >= min && numberValue <= max ? null : new Error(errorMessage || null)
    }
}

/**
 * Validates that an input is an integer or a representation thereof (can be a string).
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {number} minLen - Min value for input value.
 * @param {number} maxLen - Max value for input value.
 * @returns {function(string): (Error|null)}
 */
function validateNumber(errorMessage, minLen, maxLen) {
    return value => {
        const length = (value + '').replace('.', '').length
        const isNumber = !Number.isNaN(Number(value))

        return isNumber && Number.isInteger(parseInt(value)) && length >= minLen && length <= maxLen
            ? null
            : new Error(errorMessage || null)
    }
}

/**
 * Validates that an input is an integer or a representation thereof (can be a string). Null input is considered valid input.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {number} minLen - Min value for input value.
 * @param {number} maxLen - Max value for input value.
 * @returns {function(string): (Error|null)}
 */
function validateNumberOrNull(errorMessage, minLen, maxLen) {
    return value => {
        if (!value && typeof value !== 'number') {
            return null
        }

        const isNumber = !Number.isNaN(Number(value))
        const length = (value + '').replace('.', '').length
        return isNumber && Number.isInteger(parseInt(value)) && length >= minLen && length <= maxLen
            ? null
            : new Error(errorMessage || null)
    }
}

/**
 * Validates that a phone number is valid.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {Error} [error] - Custom error object to be thrown.
 * @returns {function(string): (Error|null)}
 */
function validatePhoneNumber(errorMessage, error) {
    return value => {
        const validPhoneNumberRegex = /^\+[0-9]{1,4}?[0-9]{6,12}$/
        return !value || (typeof value === 'string' && value.match(validPhoneNumberRegex))
            ? null
            : error || new Error(errorMessage || null)
    }
}

/**
 * Validates that a value is a string between given min and max length.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {number} minLen - Min value for input value.
 * @param {number} maxLen - Max value for input value.
 * @returns {function(string): (Error|null)}
 */
function validateString(errorMessage, minLen, maxLen) {
    return value => {
        const length = value ? (value + '').length : 0

        return length >= minLen && length <= maxLen ? null : new Error(errorMessage || null)
    }
}

/**
 * Validates the length of a string in Unicode code points is between given min and max length.
 *
 * @param {number} minLength - Minimum length of the string.
 * @param {number} maxLength - Maximum length of the string.
 * @param {string} [errorMessage] - Custom error message.
 * @returns {function(string): (Error|null)}
 */
function validateStringLength(minLength, maxLength, errorMessage) {
    return function validate(value) {
        const length = countUnicodeCodePoints(value)
        return length >= minLength && length <= maxLength ? null : new Error(errorMessage)
    }
}

/**
 * Validates that a value is a valid time.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {Error} [error] - Custom error object to be thrown.
 * @returns {function(string): (Error|null)}
 */
function validateTime(errorMessage, error) {
    return value => {
        return isValidTime(value) ? null : error || new Error(errorMessage || null)
    }
}

/**
 * Validates that a value is a valid UUID.
 *
 * @param {string} [errorMessage] - A custom error message.
 * @param {Error} [error] - Custom error object to be thrown.
 * @returns {function(string): (Error|null)}
 */
function validateUUID(errorMessage, error) {
    return value => {
        const validUuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
        return !value || (typeof value === 'string' && value.match(validUuidRegex))
            ? null
            : error || new Error(errorMessage || null)
    }
}

export {
    isEmail,
    isValidTime,
    validateArrayHasItem,
    validateArraysAreEquals,
    validateBluetooth,
    validateBluetoothWithChecksum,
    validateByteLength,
    validateEmail,
    validateExists,
    validateIsIntegerRange,
    validateIsIntegerRangeOrNull,
    validateIsPositiveNumber,
    validateMinutes,
    validateNumber,
    validateNumberOrNull,
    validatePhoneNumber,
    validateString,
    validateStringLength,
    validateTime,
    validateUUID
}
